import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getCurrentUser } from 'redux/selectors/users';

const DEFAULT_TIME_ZONE_ABBREVIATION = 'PST';

export type TimeZoneData = {
  ianaTimeZone: string;
  timeZoneAbbreviation: string;
};

const getTimeZoneAbbreviation = (tz: string, lang: string) => {
  const ianaTimeZone = tz ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const language = lang?.replace('_', '-') ?? Intl.DateTimeFormat().resolvedOptions().locale;

  try {
    const date = new Date();
    const formatter = new Intl.DateTimeFormat(language, {
      timeZone: ianaTimeZone,
      timeZoneName: 'short',
    });

    const parts = formatter.formatToParts(date);
    const timeZonePart = parts.find(part => part.type === 'timeZoneName');
    const abbreviation = timeZonePart?.value ?? DEFAULT_TIME_ZONE_ABBREVIATION;

    return abbreviation;
  } catch {
    return DEFAULT_TIME_ZONE_ABBREVIATION;
  }
};

const getIanaTimeZone = ({ timeZone, browserTimeZone }) => (
  timeZone || browserTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone
);

const useTimeZone = () => {
  const user = useSelector(getCurrentUser);
  return useMemo<TimeZoneData>(() => {
    const ianaTimeZone = getIanaTimeZone(user ?? {});
    return {
      ianaTimeZone,
      timeZoneAbbreviation: getTimeZoneAbbreviation(ianaTimeZone, user?.platformLanguage),
    };
  }, [user]);
};

export default useTimeZone;
